import axios from 'axios'
import { CONFIG_API_BASE_URL, CONFIG_SSO_SERVER_CALLBACK } from '@/config'
import {useLogger} from "@/composables/useLogger"

import { useLoaderStore } from "@/store/loader"
import { normalizeHttpError } from './httpError'

const {sendLog, sendLogInProd} = useLogger()

const httpClient = axios.create({
  baseURL: CONFIG_API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

httpClient.interceptors.request.use(
  (config) => {  
    config.metadata = {
      startTime: Date.now()
    }
    return config
  },
  (error) => {  
    return Promise.reject(error)
  }
)

// Response interceptor 
httpClient.interceptors.response.use(
  (response) => {
    // Calculate response time
    const responseTime = Date.now() - response.config.metadata.startTime
    
    return response
  },
  (error) => {  
    const normalized = normalizeHttpError(error)
    
      if (!error.response) {
        sendLogInProd(
            "error",
            "Network Error",
            normalized
        );
    }
  
    const loader = useLoaderStore()
    loader.setAlert({
      message: normalized.message,
      type: 'error',
      timeout: 5000
    })
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error(normalized)
    }
    
    return Promise.reject(normalized)
  }
)

// Helper function to check for sensitive data
function containsSensitiveData(data) {
  if (typeof data !== 'object' || !data) return false
  
  const sensitiveKeys = [
    'password', 'token', 'secret', 'key', 'authorization'
  ]
  
  const dataStr = JSON.stringify(data).toLowerCase()
  return sensitiveKeys.some(key => dataStr.includes(key))
}

/**
 * Centralized POST function with default headers and credentials
 * @param {string} url - The URL to make the POST request to
 * @param {object} payload - The data to send in the request body
 * @param {object} additionalConfig - Additional axios configuration options
 * @returns {Promise} - Returns the axios response
 */
export const post = async (url, payload, additionalConfig = {}) => {

  const defaultConfig = {
    withCredentials: true
  }

  // Merge additional config with default config
  const config = {
    ...defaultConfig,
    ...additionalConfig,
    headers: {
      ...defaultConfig.headers,
      ...additionalConfig.headers
    }
  }

  return httpClient.post(url, payload, config)
}

/**
 * Centralized GET function with default credentialspost
 * @param {string} url - The URL to make the GET request to
 * @param {object} additionalConfig - Additional axios configuration options
 * @returns {Promise} - Returns the axios response
 */authenticateToken
export const get = async (url, additionalConfig = {}) => {
  const defaultConfig = {
    withCredentials: true
  }

  // Merge additional config with default config
  const config = {
    ...defaultConfig,
    ...additionalConfig
  }

  return httpClient.get(url, config)
}


/**
 * Authenticate or renew token
 * @returns {Promise} - Returns the axios response
 */
export const authenticateToken = async () => {
  return get('/authenticateOrRenew')
}

/**
 * Logout user
 * @returns {Promise} - Returns the axios response
 */
export const logout = async () => {
  return get('/logout')
}


export const exchangeAuthenticationCodeForToken = async (code, state) => {
  return post(CONFIG_SSO_SERVER_CALLBACK, {
    code,
    state
  })
}

export const postToS3 = async (url, formData, config = {}) => {
  return axios.post(url, formData, config)
}
export const resetMFAKey = async ({_id}) => {
  return post("/resetMFAKey", {
    _id,
    "enabled": false
  })
}

export const disableUser = async ({_id}) => {
  return post("/disableUser", {
    _id
  })
}
